import {
  Box,
  Flex,
  Text,
  Image,
  Stack,
  HStack,
  Spinner,
  Button,
  Input,
  IconButton,
} from '@chakra-ui/react';
import React, { useMemo, useState, useRef, useEffect } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { RightIcon } from '@/_assets/icon-components';
import { useTranslations } from 'next-intl';
import Points from '@/_components/Points';
import { IOrderType, IRedux } from '@/_store/types';
import { Skeleton, SkeletonText } from '@/components/ui/skeleton';
import { onOpenMap } from '@/_store/common/common.slice';
import AutoGeolocate from '@/_components/AutoGeolocate';
import { useTelegram } from '@/_providers/telegram';
import SearchImg from '@/_assets/icons/search_mika.svg';
import { InputGroup } from '@/components/ui/input-group';
import { CloseIcon } from '@/_assets/icon-components';
import { MapIcon } from 'lucide-react';
import { useQueryState } from 'nuqs';
import { useRouter } from '@/i18n/navigation';
import { useLocale } from 'next-intl';

const HeaderMobile = () => {
  const [isOpen, setOpenModal] = useState(false);
  const router = useRouter();
  const locale = useLocale();

  const { webApp } = useTelegram();

  const t = useTranslations();
  const dispatch = useDispatch();

  const { settings, points, permissions } =
    useSelector(
      (state: IRedux) => ({
        settings: state.settings.data,
        points: state.common.points,
        permissions: state.session.permissions,
      }),
      shallowEqual
    );

  const activePoint = useMemo(
    () => points.find((point) => point.isActive),
    [points]
  );

  const handleOpenMap = (type: IOrderType['type'] | null) => {
    if (type === 'delivery' && points?.length > 0) {
      return setOpenModal(true);
    }

    dispatch(onOpenMap());
  };

  return (
    <>
      <AutoGeolocate />
      <Box px={4} pt={webApp?.isFullscreen ? 6 : 0}>
        <HStack justifyContent={'space-between'}>
          {!settings?.shipper_id ? (
            <HStack gap={4} flex={1}>
              <Skeleton height='48px' width='48px' rounded={12} />
              <Stack gap={1} style={{ flex: 1 }}>
                <SkeletonText noOfLines={1} width='100px' />
                <SkeletonText noOfLines={1} width='150px' />
              </Stack>
            </HStack>
          ) : (
            <HStack gap={4} flex={1}>
              <Flex flexDir={'column'}>
                <Flex
                  alignItems={'center'}
                  gap={'6px'}
                  fontSize={'sm'}
                  color='text'
                  fontWeight={600}
                  onClick={() => handleOpenMap('delivery')}
                >
                  <Text maxW={'182px'} truncate>
                    {activePoint?.address &&
                      permissions.geolocation === 'accepted' ? (
                      activePoint?.address
                    ) : permissions.geolocation === 'pending' ? (
                      <>
                        {t('location.determining_location')}
                        <Spinner size='xs' ml={1} />
                      </>
                    ) : (
                      t('add_address')
                    )}
                  </Text>
                  <RightIcon />
                </Flex>
                <Text color='#8E8E8E' fontSize={14}>
                  {t('your_location')}
                </Text>
              </Flex>
            </HStack>
          )}
          <HStack>
            <Box position='relative' w='100%' h='100%'>
              <Box
                cursor={'pointer'}
                onClick={() => router.push('/where-to-go/map')}
                rounded={'full'}
                color='#2D264B'
              >
                <MapIcon />
              </Box>
            </Box>
          </HStack>
        </HStack>
        <SearchInput />
      </Box>
      <Points
        isOpen={isOpen && points?.length > 0}
        onClose={() => setOpenModal(false)}
      />
    </>
  );
};

const SearchInput = () => {
  const [search, setSearch] = useQueryState('search', { defaultValue: '' });
  const [isExpanded, setIsExpanded] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const t = useTranslations();

  useEffect(() => {
    if (isExpanded) {
      inputRef.current?.focus();
    }
  }, [isExpanded]);

  const containerStyles = {
    mt: '9px',
    w: 'full',
    minH: '44px',
    rounded: 16,
    overflow: 'hidden',
    transition: 'background-color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease',
    _focusWithin: {
      bg: 'white',
      borderColor: 'gray.300',
      boxShadow: '0 0 0 2px var(--chakra-colors-gray-200)',
    },
  };

  const buttonStyles = {
    position: 'absolute' as const,
    inset: 0,
    w: 'full',
    minH: '44px',
    rounded: 16,
    variant: 'subtle' as const,
    gap: 2,
    px: 4,
    opacity: isExpanded ? 0 : 1,
    pointerEvents: isExpanded ? ('none' as const) : ('auto' as const),
    transition: 'opacity 0.2s ease',
  };

  const inputWrapperStyles = {
    position: 'absolute' as const,
    inset: 0,
    w: 'full',
    h: 'full',
    opacity: isExpanded ? 1 : 0,
    pointerEvents: isExpanded ? ('auto' as const) : ('none' as const),
    transition: 'opacity 0.2s ease',
  };

  return (
    <Box {...containerStyles} position='relative'>
      <Button {...buttonStyles} size='sm' onClick={() => setIsExpanded(true)}>
        <HStack gap={2}>
          <Image src={SearchImg.src} alt='' w={5} h={5} opacity={0.7} />
          <Text>{t('search_in_city')}</Text>
        </HStack>
      </Button>

      <Box {...inputWrapperStyles}>
        <InputGroup
          h='full'
          paddingLeft='24px'
          w='full'
          startElement={<Image src={SearchImg.src} alt='' w={5} h={5} opacity={0.7} />}
          startElementProps={{ left: '12px' }}
          endElement={
            <IconButton
              aria-label='Close search'
              size='sm'
              rounded='xl'
              variant='ghost'
              onClick={() => {
                setIsExpanded(false);
                setSearch('');
              }}
            >
              <CloseIcon />
            </IconButton>
          }
        >
          <Input
            ref={inputRef}
            h='full'
            minH='44px'
            rounded={16}
            bg='transparent'
            border='none'
            _focusVisible={{ outline: 'none', boxShadow: 'none' }}
            value={search}
            onChange={(e) => setSearch(e.target.value || '')}
            placeholder='Найти в городе'
            color='gray.700'
            fontSize='md'
            onBlur={() => {
              if (!search) setIsExpanded(false);
            }}
            _placeholder={{ color: 'gray.500' }}
          />
        </InputGroup>
      </Box>
    </Box>
  );
};

export default HeaderMobile;
